import React, { useEffect, useRef, useMemo } from "react";

import { ReportAnswer } from "../../../graphql/types";
import { TemplateQuestionFragment } from "../../../graphql/fragments";

import useTranslations from "../../../hooks/useTranslations";

import { Badge, Textarea, Radio, RadioGroup, Input } from "../../wombat-ui";

import {
  getReportAnswersByRole,
  TemplateQuestionImplProps,
  TemplateQuestionPreviewImplProps,
} from "../TemplateQuestion";
import TemplateQuestionTitle from "./utils/TemplateQuestionTitle";
import Answer from "./utils/Answer";

type TemplateQuestionEvaluationOptions = {
  value: string;
  label: string;
};

type TemplateQuestionEvaluationValue = {
  selector: string;
  comment: string;
  timeSpent: number;
};

type TemplateQuestionEvaluation = {
  options: TemplateQuestionEvaluationOptions[];
  defaultValue: TemplateQuestionEvaluationValue;
} & TemplateQuestionFragment;

export function isTemplateQuestionEvaluation(
  q: TemplateQuestionFragment
): q is TemplateQuestionEvaluation {
  return q.type === "TemplateQuestionEvaluation";
}

export function parseQuestion(
  question: TemplateQuestionFragment
): TemplateQuestionEvaluation {
  return question as TemplateQuestionEvaluation;
}

type ReportAnswerEvaluation = {
  value: TemplateQuestionEvaluationValue;
} & ReportAnswer;

export function parseAnswers(
  answers: ReportAnswer[]
): ReportAnswerEvaluation[] {
  return answers as ReportAnswerEvaluation[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface TemplateQuestionEvaluationProps extends TemplateQuestionImplProps {}

export const TemplateQuestionEvaluation: React.FC<TemplateQuestionEvaluationProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    mad,
    register,
    status,
    displayIndex,
    role,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
      [rawAnswers]
    );

    const firstRadioRef = useRef(null);
    useEffect(() => {
      if (autoFocus) firstRadioRef.current?.focus();
    }, [firstRadioRef.current, autoFocus]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <TemplateQuestionTitle
              title={question.label}
              description={question.description}
              displayIndex={displayIndex}
              referents={question.referents}
            />
          }
          description={
            t("reports.evaluationQuestion", {
              maxRankIndex: question.options.length,
            }) as string
          }
        >
          {question.options.map((opt, index) => (
            <Radio
              defaultChecked={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
                  ?.selector === opt.value
              }
              key={opt.value}
              className={
                opt?.isGoal ? "text-warning-500 bg-warning-100" : undefined
              }
              name={`${name}.value.selector`}
              help={
                opt?.isGoal
                  ? (t("templates.targetLevel") as string)
                  : undefined
              }
              label={`${opt.label} (${index + 1})`}
              id={`${question.label}_${opt.value}`}
              value={opt.value}
              disabled={!needsToBeFilled}
              variant="boxed"
              ref={(ref) => {
                if (index === 0) firstRadioRef.current = ref;
                register(ref);
              }}
            />
          ))}
        </RadioGroup>
        {question.timeTracked && needsToBeFilled && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            min={0}
            disabled={!needsToBeFilled}
            id={`${question.id}-timeTracked`}
            name={`${name}.timeSpent`}
            ref={register({ valueAsNumber: true })}
            defaultValue={answers?.talent?.[0]?.timeSpent || undefined}
          />
        )}
        {needsToBeFilled && (
          <div className="max-w-2xl pt-4">
            <Textarea
              defaultValue={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
                  .comment
              }
              ref={register()}
              name={`${name}.value.comment`}
              label={t("reports.comments") as string}
              id={question.id}
              rows={2}
              help={t("reports.commentsHelp") as string}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.talentAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.talent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={
                    <Badge>
                      {
                        question.options.find(
                          (o) => o.value === a.value.selector
                        )?.label
                      }
                    </Badge>
                  }
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
        {answers.referent.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referent.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={
                    <Badge>
                      {
                        question.options.find(
                          (o) => o.value === a.value.selector
                        )?.label
                      }
                    </Badge>
                  }
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type TemplateQuestionEvaluationPreviewProps = TemplateQuestionPreviewImplProps;

export const TemplateQuestionEvaluationPreview: React.FC<TemplateQuestionEvaluationPreviewProps> = React.memo(
  ({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <RadioGroup
          title={
            <TemplateQuestionTitle
              title={question.label}
              displayIndex={displayIndex}
            />
          }
          description={
            t("reports.evaluationQuestion", {
              maxRankIndex: question.options.length,
            }) as string
          }
        >
          {question.options.map((opt, index) => (
            <Radio
              key={`${opt.value}`}
              label={`${opt.label} (${index + 1})`}
              id={`${question.label}_${opt.value}`}
              disabled
              variant="boxed"
            />
          ))}
        </RadioGroup>
      </div>
    );
  }
);
